import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { problemDetail } from '@up2d8/api-client';
import type { WebsiteFeedSettings } from '@up2d8/contracts';
import { useSession } from '../../shared/session';
import { Card, CardHead, CardPad, Field, Input, Note, Stack, SwitchRow } from '../admin-kit';

/**
 * De openbare websitefeed (WEB-1, ADR-0097): open of dicht, en de adressen voor de webmaster.
 *
 * Een kleine kaart op de pagina van de organisatie, naast de naam en het logo — die twee staan ook
 * bovenaan de feed. Alleen voor wie `communication.website_feed.manage` heeft; de Beheerder heeft
 * dat vanzelf.
 *
 * De adressen komen van de server: alleen die weet op welk domein de API voor de buitenwereld staat.
 * Ze staan er ook als de feed dicht is, zodat een webmaster zijn script al kan klaarzetten; tot de
 * feed opengaat, krijgt hij een 404.
 */
export function WebsiteFeedCard({ onSaved }: { onSaved: (message: string) => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['website-feed'],
    queryFn: async (): Promise<WebsiteFeedSettings> => {
      const { data, error } = await api.GET('/api/v1/website-feed', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as WebsiteFeedSettings;
    },
  });

  const save = useMutation({
    mutationFn: async (enabled: boolean): Promise<WebsiteFeedSettings> => {
      const { data, error } = await api.PUT('/api/v1/website-feed', { body: { enabled } });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
      return data as WebsiteFeedSettings;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(['website-feed'], next);
      onSaved(next.enabled ? t('websiteFeed.opened') : t('websiteFeed.closed'));
    },
  });

  const current = settings.data;

  return (
    <Card testId="website-feed-card">
      <CardHead title={t('websiteFeed.title')} />
      <CardPad>
        <Stack>
          <SwitchRow
            title={t('websiteFeed.toggle')}
            text={t('websiteFeed.hint')}
            checked={current?.enabled ?? false}
            disabled={!current || save.isPending}
            onChange={(enabled) => save.mutate(enabled)}
            testId="website-feed-enabled"
          />
          {save.isError ? (
            <Note tone="danger" role="alert">
              {problemDetail(save.error) ?? t('errors.generic')}
            </Note>
          ) : null}
          {settings.isError ? (
            <Note tone="danger" role="alert">
              {t('errors.generic')}
            </Note>
          ) : null}
          {current ? (
            <>
              <Field label={t('websiteFeed.feedUrl')} hint={t('websiteFeed.feedUrlHint')}>
                {(props) => (
                  <Input
                    {...props}
                    value={current.feedUrl}
                    readOnly
                    onFocus={(event) => event.target.select()}
                    data-testid="website-feed-url"
                  />
                )}
              </Field>
              <Field label={t('websiteFeed.legacyUrl')} hint={t('websiteFeed.legacyUrlHint')}>
                {(props) => (
                  <Input
                    {...props}
                    value={current.legacyUrl}
                    readOnly
                    onFocus={(event) => event.target.select()}
                    data-testid="website-feed-legacy-url"
                  />
                )}
              </Field>
            </>
          ) : null}
        </Stack>
      </CardPad>
    </Card>
  );
}
